/**
 * Account mutation hooks (FE-3, PHASE1 §4 accounts + manual transaction, §7.3
 * invalidation).
 *
 * Anything that changes WHICH accounts (or bookings) the analysis surfaces see
 * — close/reopen, merge, hide, remove, unhide — goes through
 * `invalidateAccountShapeChange`; a cheap create/reorder only touches
 * `['accounts']`. Manual transaction create invalidates `['transactions']` +
 * `['inbox']` (NOT accounts — a manual booking does not move the CLBD balance).
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk } from '../queryKeys.ts';
import type {
  AccountBalanceSet,
  AccountCreate,
  AccountMergeIn,
  AccountMergeOut,
  AccountOut,
  AccountRemoveIn,
  AccountRemoveOut,
  AccountUpdate,
  RemovalReport,
  TransactionCreate,
  TransactionDetailOut,
} from '../types.ts';

/** Invalidate `['transfers','candidates',*]` and `['transfers','linked']`
 * regardless of query params (mirrors `useTransfers.ts`'s `CANDIDATES_PREFIX`). */
const TRANSFERS_PREFIX = ['transfers'] as const;

/** POST /api/accounts — create a manual account (optional initial CLBD balance). */
export function useCreateAccount(): UseMutationResult<AccountOut, Error, AccountCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AccountCreate) => api.post<AccountOut>('/api/accounts', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.accounts });
    },
  });
}

export interface AccountUpdateVars {
  id: number;
  body: AccountUpdate;
}

/**
 * PATCH /api/accounts/{id} — rename / retype / re-IBAN / close / reopen.
 *
 * Closing an account (`archived`) drops it out of the net-worth series
 * (`services/reports_query.py`) and stops its sync, so this cannot invalidate
 * `['accounts']` alone — a rename over-invalidates a little, a close that only
 * refreshed the account list would leave Berichte showing the closed account's
 * balance for the full `staleTime`.
 */
export function useUpdateAccount(): UseMutationResult<AccountOut, Error, AccountUpdateVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: AccountUpdateVars) =>
      api.patch<AccountOut>(`/api/accounts/${id}`, body),
    onSuccess: () => invalidateAccountShapeChange(qc),
  });
}

export interface AccountBalanceVars {
  id: number;
  body: AccountBalanceSet;
}

/** POST /api/accounts/{id}/balance — write a CLBD snapshot + mirror current_balance. */
export function useSetAccountBalance(): UseMutationResult<AccountOut, Error, AccountBalanceVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: AccountBalanceVars) =>
      api.post<AccountOut>(`/api/accounts/${id}/balance`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.accounts });
      void qc.invalidateQueries({ queryKey: qk.reports });
      void qc.invalidateQueries({ queryKey: qk.budgets });
    },
  });
}

export interface AccountMergeVars {
  sourceId: number;
  body: AccountMergeIn;
}

/**
 * POST /api/accounts/{id}/merge — fold the source's history into the target
 * (duplicate repair after re-connecting an IBAN over a second channel). A
 * `dry_run` is a pure preview, so it invalidates nothing; a real merge touches
 * accounts, the ledger, and possibly a connection's account mapping.
 */
export function useMergeAccounts(): UseMutationResult<AccountMergeOut, Error, AccountMergeVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sourceId, body }: AccountMergeVars) =>
      api.post<AccountMergeOut>(`/api/accounts/${sourceId}/merge`, body),
    onSuccess: (_data, vars) => {
      if (vars.body.dry_run) return;
      invalidateAccountShapeChange(qc);
    },
  });
}

/** Invalidate every cache a close / merge / remove / unhide can touch
 * (ACCOUNT-REMOVAL-SPEC §9): accounts, transactions, inbox, connections,
 * reports, budgets, transfers, contracts, receipts.
 *
 * The list is deliberately shared rather than tuned per mutation: a merge
 * re-parents Contracts, ImportBatches and EmailIngestConfigs
 * (`services/account_merge.py`) exactly like a removal detaches receipts back
 * into the Beleg-Eingang (§3.4 / service step 6, `transaction_id=None`), and
 * the previous per-hook subsets each forgot a different surface. */
function invalidateAccountShapeChange(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: qk.accounts });
  void qc.invalidateQueries({ queryKey: qk.transactions });
  void qc.invalidateQueries({ queryKey: qk.inbox });
  void qc.invalidateQueries({ queryKey: qk.connections });
  void qc.invalidateQueries({ queryKey: qk.reports });
  void qc.invalidateQueries({ queryKey: qk.budgets });
  void qc.invalidateQueries({ queryKey: TRANSFERS_PREFIX });
  void qc.invalidateQueries({ queryKey: qk.contracts });
  void qc.invalidateQueries({ queryKey: qk.receipts });
}

export interface AccountRemoveVars {
  id: number;
  body: AccountRemoveIn;
}

/**
 * POST /api/accounts/{id}/remove — hide (reversible) or hard-delete an account
 * (ACCOUNT-REMOVAL-SPEC §5/§6). A `dry_run` is a pure preview, so it
 * invalidates nothing; a real removal touches the whole downstream set.
 */
export function useRemoveAccount(): UseMutationResult<RemovalReport, Error, AccountRemoveVars> {
  const qc = useQueryClient();
  return useMutation({
    // The endpoint answers `{report, account}` (§6, like `merge`); callers only
    // ever render the report, and the refreshed account arrives through the
    // invalidations below — so unwrap here rather than in every consumer.
    mutationFn: async ({ id, body }: AccountRemoveVars) =>
      (await api.post<AccountRemoveOut>(`/api/accounts/${id}/remove`, body)).report,
    onSuccess: (_data, vars) => {
      if (vars.body.dry_run) return;
      invalidateAccountShapeChange(qc);
    },
  });
}

/** POST /api/accounts/{id}/unhide — reverse `mode='hide'` (409 `account_removed`
 * for wiped tombstones — the caller surfaces the error message). */
export function useUnhideAccount(): UseMutationResult<AccountOut, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<AccountOut>(`/api/accounts/${id}/unhide`),
    onSuccess: () => invalidateAccountShapeChange(qc),
  });
}

/** POST /api/accounts/reorder — position in `ordered_ids` becomes `sort_order`. */
export function useReorderAccounts(): UseMutationResult<AccountOut[], Error, number[]> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: number[]) =>
      api.post<AccountOut[]>('/api/accounts/reorder', { ordered_ids: orderedIds }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.accounts });
    },
  });
}

/**
 * POST /api/transactions — manual transaction entry. `bucket_id` set → one user
 * split, review_status=reviewed; omitted → review_status=pending (lands in the
 * inbox). Invalidates the transaction list and the inbox.
 */
export function useCreateManualTransaction(): UseMutationResult<
  TransactionDetailOut,
  Error,
  TransactionCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TransactionCreate) =>
      api.post<TransactionDetailOut>('/api/transactions', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.transactions });
      void qc.invalidateQueries({ queryKey: qk.inbox });
      void qc.invalidateQueries({ queryKey: qk.reports });
      void qc.invalidateQueries({ queryKey: qk.budgets });
    },
  });
}
